import React, {Component, Fragment} from 'react';
import {Button, FormHelperText} from '@material-ui/core';
import {Booking, BookingService} from 'services/entities/booking';
import Form, {FormStatus} from 'components/forms/Form';
import {ValidationGroup, Validators} from 'util/validation';
import ProgressSpinner from 'components/ProgressSpinner';
import CheckBoxField from 'components/forms/CheckBoxField';
import {updateBookingServices} from 'services/bookings';
import {handleError} from 'util/errors';
import './index.scss';
import {SuccessIcon} from 'components/Icon';

interface BookingServicesFormState {
  status: FormStatus;
}

interface BookingServicesFormProps {
  booking: Booking;
}

interface BookingServicesFormModel {
  services: BookingService[];
}

export default class BookingServicesForm extends Component<BookingServicesFormProps, BookingServicesFormState> {
  state = {
    status: FormStatus.None
  };

  private readonly validation: ValidationGroup;
  private readonly model:BookingServicesFormModel = { services: [] };

  constructor(props:BookingServicesFormProps) {
    super(props);
    this.validation = new ValidationGroup(this.model);
    this.validation.addField('services', {'required': Validators.required});
  }

  onServiceCheckedChange = (service: BookingService, checked: boolean) => {
    const services = this.model.services;
    const index = services.indexOf(service);
    if(checked) {
      if(service === BookingService.Everything) {
        services.length = 0;
        services.push(service);
      } else if(index < 0) {
        services.push(service);
      }
    } else if(index >= 0) {
      services.splice(index, 1);
    }
    this.validation.validateField('services', true);
    this.setState({ status: FormStatus.None });
  };

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    updateBookingServices(this.props.booking.code, this.model.services).then(
      () => this.setState({status: FormStatus.Success}),
      () => {
        this.setState({status: FormStatus.Failure});
        handleError('Failed to save assistance options');
      }
    );
  };

  renderSuccessMessage() {
    return <div className="booking-services-form success">
      <h3><SuccessIcon/> Your request has been submitted.</h3>
      <p>A member of our team will be in touch to assist you.</p>
    </div>;
  }

  renderOption(service: BookingService, label: string, details: string, price: string) {
    let enabled = this.state.status === FormStatus.None || this.state.status === FormStatus.Failure;
    if(service !== BookingService.Everything && this.model.services.indexOf(BookingService.Everything) >= 0) {
      enabled = false;
    }
    return <div className="field">
      <CheckBoxField
        disabled={!enabled}
        label={<Fragment>
          <span>{label}</span>
          <span className="details">{details}</span>
        </Fragment>}
        name={service.toString()}
        checked={this.model.services.indexOf(service) >= 0}
        validationGroup={this.validation}
        onChange={(checked) => this.onServiceCheckedChange(service, checked)} />
      <div className="price">{price}</div>
    </div>;
  }

  renderForm() {
    const enabled = this.state.status === FormStatus.None || this.state.status === FormStatus.Failure;
    const errors = this.validation.getFieldErrors('services');
    return <Form className="booking-services-form" validationGroup={this.validation} onSubmit={this.onSubmit}>
      { this.renderOption(BookingService.Ideas, 'I want imagination help',
        'A member of the team will share some inspiration to get the ideas flowing', 'Free') }
      { this.renderOption(BookingService.Design, 'I have ideas - I want a doer to bring things to life',
        'A member of the team will follow your ideas to design your space how you like', 'Ranges from £25 - $75') }
      { this.renderOption(BookingService.BrandAssets, 'I want great brand assets',
        'We will provide you with beautiful assets that match your brand', 'Ranges from £50 - £100') }
      { this.renderOption(BookingService.Video, 'I want a great video',
        'We will provide you with a beautiful video to represents your brand', 'Ranges from £250 - £400') }
      { this.renderOption(BookingService.Logo, 'I need a logo',
        'We will provide you a logo mark that represents your brand', 'Ranges from £200 - £400') }
      { this.renderOption(BookingService.Everything, 'I want help with every single element',
        'We will provide you with everything your space and brand needs to shine', 'Ranges from £300 - £500') }
      { errors && errors[0] === 'required' && <FormHelperText className="Mui-error">At least one service must be selected</FormHelperText> }
      <div className="actions">
        <Button type="submit" disabled={!enabled}>
          {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
          Submit enquiry
        </Button>
      </div>
    </Form>;
  }

  render() {
    if(this.state.status === FormStatus.Success) {
      return this.renderSuccessMessage();
    } else {
      return this.renderForm();
    }
  }
}